import { Ref } from "vue";
import { defineStore } from "pinia";
import { Dictionary } from "../models/dictionary";
import intersection from "lodash.intersection";
import {
    useDictLoader,
    useUsersLoader,
    useClientsDictLoader,
    useDictLoaderByDate,
} from "../utils/api";

import dayjs from "dayjs";

import {
    CGSSingleSwingDoorCode,
    CGFSingleSwingDoorCode,
    CGSDoubleSwingDoorsCode,
    CGFDoubleSwingDoorsCode,
    KompasDoors,
    KompasSystemCodes,
} from "../../js/app/constants";

export const useDictStore = defineStore("dict", () => {
    console.debug("init dict store");

    const dicts = {
        usStates: [],
        dealTypes: [],
        spaceDescriptions: [],
        systemTypes: [],
        installationPlaces: [],
        installationUsage: [],
        finishColors: [],
        structureTypes: [],
        doorTypes: [],
        glassTypes: [],
        glassSort: [],
        glassThick: [],
        statesGlassPrices: [],
        hardwareTypes: [],
        hardwareValues: [],
        additionalCostTypeExtra: [],
        additionalCostTypes: [],
        transomTypes: [],
        fillerTypes: [],
        aluminumMaterialTypes: [],
        additionalCostValues: [],
        managers: [],
        estimators: [],
        clients: [],
        multiplicator: [],
        overheadCostValues: [],
        horizontalMullionsSizes: [],
        verticalMullionsSizes: [],
        mullionsTrackPrice: [],
        deliveryPriceMin: [],
        deliveryPriceSystemMin: [],
        deliveryDiscountLimits: [],
        glassWeight: [],
        glassDefaultThick: [],
    };

    // для загрузки по f5

    // useUserStore().checkLogin().then(() => {
    //     console.debug('wait for logged user to init dicts', {
    //         'dictLoaded': dictLoaded,
    //         'user logged': useUserStore().isLogged
    //     });
    //     if (!dictLoaded && useUserStore().isLogged) {
    //         console.debug(' before load all dicts');
    //         loadAllDicts();
    //     }
    // })

    function getAllDicts(): string[] {
        return Object.keys(dicts);
    }

    function clearAllDicts() {
        getAllDicts().forEach((name) => {
            localStorage.removeItem("dict." + name + ".cache");
            localStorage.removeItem("dict." + name);
        });
    }

    function loadAllDicts() {
        console.debug("load all dicts");
        getAllDicts().forEach((dict) => loadDict(dict));
    }

    async function loadAllDictsCurrentDay() {
        console.debug("load all dicts");
        for (const dict of getAllDicts()) {
            await loadDictByDate(dict, dayjs().format("YYYY-MM-DD"));
        }
    }

    async function loadDict(name: string, force = false) {
        loadDictLocal(name);

        if (dicts[name] && dicts[name].length && !force) {
            return;
        }

        let data = [];

        switch (name) {
            case "estimators":
                await loadUsers("estimators");
                break;
            case "managers":
                await loadUsers("managers");
                break;
            case "clients":
                await loadClients();
                break;
            default:
                data = await useDictLoader(name);
                if (!data) {
                    // console.log(`dict ${name} load error (empty data)`);
                    return;
                }
                dicts[name] = data;
        }
        // ждем пока загрузится
        // while (dicts[name].length === 0) {};

        storeDictLocal(name);
    }

    function loadDictLocal(name: string): void {
        // проверяем на истек ли кэш для справочника
        const dictCache =
            localStorage.getItem("dict." + name + ".cache") != null
                ? new Date(
                    Number(localStorage.getItem("dict." + name + ".cache")),
                )
                : null;
        // проверяем, не протух ли кэш для справочника
        if (dictCache && dictCache > new Date()) {
            dicts[name] = JSON.parse(localStorage.getItem("dict." + name));
        } else dicts[name] = [];
    }

    function loadDictLocalByDate(name: string, date: string): void {
        // проверяем на истек ли кэш для справочника
        const dictCache =
            localStorage.getItem("dict." + name + date + ".cache") != null
                ? new Date(
                    Number(
                        localStorage.getItem(
                            "dict." + name + date + ".cache",
                        ),
                    ),
                )
                : null;
        // проверяем, не протух ли кэш для справочника
        if (dictCache && dictCache > new Date()) {
            dicts[name] = JSON.parse(
                localStorage.getItem("dict." + name + date),
            );
        } else dicts[name] = [];
    }

    function storeDictLocalByDate(name: string, date: string): void {
        localStorage.setItem(
            "dict." + name + date,
            JSON.stringify(dicts[name]),
        );
        localStorage.setItem(
            "dict." + name + date + ".cache",
            (new Date().getTime() + 30 * 60 * 1000).toString(),
        ); // +30 мин
    }

    function storeDictLocal(name: string): void {
        localStorage.setItem("dict." + name, JSON.stringify(dicts[name]));
        localStorage.setItem(
            "dict." + name + ".cache",
            (new Date().getTime() + 1440 * 60 * 1000).toString(),
        ); // +24h
    }

    function loadAllDictsByDate(date, dateWithTime) {
        console.debug("load all dicts by date");
        getAllDicts().forEach((dict) =>
            loadDictByDate(dict, date, dateWithTime),
        );
    }

    async function loadDictByDate(
        name: string,
        date: string,
        dateWithTime: string,
    ) {
        //loadDictLocalByDate(name, date);

        /*if (dicts[name] && dicts[name].length && !force) {
            return;
        }*/

        let data = [];
        switch (name) {
            case "estimators":
                await loadUsers("estimators");
                break;
            case "managers":
                await loadUsers("managers");
                break;
            case "clients":
                await loadClients();
                break;
            default:
                data = await useDictLoaderByDate(name, date, dateWithTime);
                if (!data) {
                    // console.log(`dict ${name} load error (empty data)`);
                    return;
                }
                dicts[name] = data;
        }

        storeDictLocal(name);
        //storeDictLocalByDate(name, date);
    }

    async function loadClients() {
        const data = await useClientsDictLoader();

        if (!data) {
            // console.log(`dict clients load error (empty data)`);
            return;
        }

        dicts["clients"] = data.data;
    }

    async function loadUsers(role) {
        const data = await useUsersLoader(role);

        if (!data) {
            // console.log(`dict ${role} load error (empty data)`);
            return;
        }

        switch (role) {
            case "estimators":
                dicts["estimators"] = data.data;
                break;
            case "managers":
                dicts["managers"] = data.data;
                break;
        }
    }

    function filterAppliedTo(
        dictName: string,
        appliedTo: string | string[] = null,
    ): Array<Dictionary> {
        loadDict(dictName);

        const dataArray = dicts[dictName];

        if (!dataArray) {
            return [];
        }

        if (!appliedTo) {
            return dataArray;
        }
        appliedTo = typeof appliedTo === "string" ? [appliedTo] : appliedTo;
        return dataArray.filter((item) =>
            item?.applyTo === undefined || !item?.applyTo?.length
                ? true
                : intersection(item.applyTo, appliedTo).length ===
                appliedTo.length,
        );
    }

    function getUsStates(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("usStates", appliedTo);
    }

    function findState(state: String): Dictionary | undefined {
        return getUsStates().find((item) => item.code === state);
    }

    function getDealTypes(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("dealTypes", appliedTo);
    }

    // todo заменитьно тексту на installationUsage, 2 раза одни справочник
    function getSystemUsages(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("installationUsage", appliedTo);
    }

    function getManagers(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("managers", appliedTo);
    }

    function getEstimators(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("estimators", appliedTo);
    }

    function getClients(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("clients", appliedTo);
    }

    function getSystemTypes(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("systemTypes", appliedTo);
    }

    function getFinishColors(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("finishColors", appliedTo);
    }

    function getInstallationPlaces(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("installationPlaces", appliedTo);
    }

    function getInstallationUsage(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("installationUsage", appliedTo);
    }

    function getSpaceDescriptions(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("spaceDescriptions", appliedTo);
    }

    function getStructureTypes(
        appliedTo: string | string[] | undefined = undefined,
    ): Dictionary[] {
        return filterAppliedTo("structureTypes", appliedTo);
    }

    function getDoorTypes(appliedTo: string | string[] = null): Dictionary[] {
        return filterAppliedTo("doorTypes", appliedTo);
    }

    function getGlassTypes(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("glassTypes", appliedTo);
    }

    function getGlassPricesByStates(
        appliedTo: string | string[] = null,
    ): Dictionary[] {
        return filterAppliedTo("statesGlassPrices", appliedTo);
    }

    function getGlassSorts(appliedTo: string[] = null): Dictionary[] {
        return filterAppliedTo("glassSort", appliedTo);
    }

    function getGlassThickness(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("glassThick", appliedTo);
    }

    function getOverheadCosts(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("overheadCostValues", appliedTo);
    }

    function getGlassThicknessIncludeDoorCondition(
        appliedTo: string = null,
        doorCode: string = null,
    ): Dictionary[] {
        let glassThickness = getGlassThickness(appliedTo);
        if (doorCode == null) {
            return glassThickness;
        } else {
            if (KompasDoors.includes(doorCode)) {
                glassThickness = glassThickness.filter(function (v) {
                    if (v.code == "1-4") {
                        return v;
                    }
                });
            }
            if (
                [
                    CGSSingleSwingDoorCode,
                    CGFSingleSwingDoorCode,
                    CGSDoubleSwingDoorsCode,
                    CGFDoubleSwingDoorsCode,
                ].includes(doorCode)
            ) {
                glassThickness = glassThickness.filter(function (v) {
                    if (v.code == "1-2" || v.code == "3-8") {
                        return v;
                    }
                });
            }
        }

        return glassThickness;
    }

    function getHardwareTypes(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("hardwareTypes", appliedTo);
    }

    function getHardwareValues(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("hardwareValues", appliedTo);
    }

    function getCostTypes(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("additionalCostTypes", appliedTo);
    }

    function getTransomTypes(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("transomTypes", appliedTo);
    }

    function getFillerTypes(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("fillerTypes", appliedTo);
    }

    function getAluminumMaterialTypes(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("aluminumMaterialTypes", appliedTo);
    }

    function getAdditionalCosts(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("additionalCostTypes", appliedTo);
    }

    function getAdditionalCostExtras(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("additionalCostTypeExtra", appliedTo);
    }
    function getGlassDefaultThick(appliedTo: string = null): Dictionary[] {
        return filterAppliedTo("glassDefaultThick", appliedTo);
    }

    function getAdditionalCostValues(appliedTo: string = null): Dictionary[] {
        /**
         * В dicts есть/должно быть поле appliedTo, которое содержит массив строк,
         * filterAppliedTo возвращает массив элементов dicts[dictName], у которых appliedTo содержит все строки из appliedTo
         * Если appliedTo не передан, возвращаются все элементы dicts[dictName]
         *
         * В данном случае dictName - additionalCostValues, поэтому возвращаются все элементы dicts[additionalCostValues],
         * которые в appliedTo содержат additionalCostTypeCode, например, oh-dr-01
         *
         * Например, вот такой объект: {
                "code": "oh-dr-01",
                "title": "Drawing",
                "applyTo": [
                    "oh-dr"
                ],
                "unit": "100 sq.ft.",
                "price": "50",
                "priceB2C": "130",
                "priceB2B": "130"
            }
         *
         * */
        return filterAppliedTo("additionalCostValues", appliedTo);
    }

    function getHorizontalMullionsSizes(
        appliedTo?: string | string[],
    ): Dictionary[] {
        return filterAppliedTo("horizontalMullionsSizes", appliedTo);
    }

    function getVerticalMullionsSizes(
        appliedTo?: string | string[],
    ): Dictionary[] {
        return filterAppliedTo("verticalMullionsSizes", appliedTo);
    }

    function getMullionsTrackPrice(
        appliedTo?: string | string[],
    ): Dictionary[] {
        return filterAppliedTo("mullionsTrackPrice", appliedTo);
    }

    function findMullionsTrackPrice(structureCode: string): Dictionary {
        return getFromDictByCode(getMullionsTrackPrice(), structureCode);
    }

    function findStateGroupCode(stateCode?: string): string {
        const state = getUsStates().find((item) => item.code === stateCode);

        if (!state) {
            return "";
        }

        const stateGroupCode = state.applyTo ? state.applyTo[0] : "";

        return stateGroupCode;
    }

    // prices
    function findGlassPrice(
        glassTypeCode: string,
        glassThicknessCode: string,
        stateCode?: string,
    ): Dictionary {
        const stateGroupCode = findStateGroupCode(stateCode).toLowerCase();

        console.log("glassThicknessCode", glassThicknessCode);
        console.log("glassTypeCode", glassTypeCode);
        console.log("stateGroupCode", stateGroupCode);

        const priceCode = `${glassThicknessCode}-${glassTypeCode}-${stateGroupCode}`;

        return getFromDictByCode(getGlassPricesByStates(""), priceCode);
    }

    function findGlassSort(installationSystem: string) {
        const glasses = getGlassPricesByStates(installationSystem);

        console.log("installationSystem", installationSystem);
        console.log("glasses", glasses);

        const formattedGlasses = glasses.map((glass) => {
            console.log("glass title", glass.title);

            const code = glass.code.replace(/^\d+-\d+-/, "");

            return {
                ...glass,
                code: code.slice(0, -3),
                title: glass.title.substring(glass.title.indexOf(" ") + 1),
            };
        });

        return formattedGlasses.filter(
            (item, index, self) =>
                index === self.findIndex((t) => t.title === item.title),
        );
    }

    function findDoorHardwarePrice(
        hardwareTypeCode: string,
        hardwareValueCode: string,
    ): Dictionary {
        return getFromDictByCode(
            getHardwareValues(hardwareTypeCode),
            hardwareValueCode,
        );
    }

    function findDoorAdditionalCostsPrice(
        additionalCostsCode: string,
    ): Dictionary {
        return getFromDictByCode(
            getAdditionalCostValues(),
            additionalCostsCode,
        );
    }

    function findOutOfSquarePrice(additionalCostsCode: string): Dictionary {
        return getFromDictByCode(
            getAdditionalCostValues(),
            additionalCostsCode,
        );
    }

    function findAssetsPrice(additionalCostsCode: string): Dictionary {
        return getFromDictByCode(
            getAdditionalCostValues(),
            additionalCostsCode,
        );
    }

    function findAluminumFillerPrice(aluminumTypeCode: string): Dictionary {
        return getFromDictByCode(getAluminumMaterialTypes(), aluminumTypeCode);
    }

    function findTransomHardwarePrice(
        hardwareTypeCode: string,
        hardwareValueCode: string,
    ): Dictionary {
        return getFromDictByCode(
            getHardwareValues(hardwareTypeCode),
            hardwareValueCode,
        );
    }

    function findMountHardwarePrice(
        hardwareTypeCode: string,
        hardwareValueCode: string,
    ): Dictionary {
        console.log("hardwareTypeCode", hardwareTypeCode);
        console.log("hardwareValueCode", hardwareValueCode);

        return getFromDictByCode(
            getHardwareValues(hardwareTypeCode),
            hardwareValueCode,
        );
    }

    function findAdditionalCostPrice(
        additionalCostTypeCode: string,
        additionalCostValueCode: string,
    ): Dictionary {
        return getFromDictByCode(
            getAdditionalCostValues(additionalCostTypeCode),
            additionalCostValueCode,
        );
    }

    function findHardwarePrice(
        hardwareTypeCode: string,
        hardwareValueCode: string,
    ): Dictionary {
        return getFromDictByCode(
            getHardwareValues(hardwareTypeCode),
            hardwareValueCode,
        );
    }

    function findOverheadCostPrice(overheadStructureCode: string): Dictionary {
        return getFromDictByCode(getOverheadCosts(), overheadStructureCode);
    }

    function findHorizontalMullionsQty(height: number): Dictionary | undefined {
        const horizontalMullions = getHorizontalMullionsSizes();

        return horizontalMullions.find(
            (mullion) =>
                (mullion.hightMin || 0) <= height &&
                (mullion.hightMax || 0) >= height,
        );
    }

    function findVerticalMullionsQty(width: number): Dictionary | undefined {
        const verticalMullions = getVerticalMullionsSizes();

        return verticalMullions.find(
            (mullion) =>
                (mullion.sizeMin || 0) <= width &&
                (mullion.sizeMax || 0) >= width,
        );
    }

    function findMullionTrack(width: number): Dictionary | undefined {
        const mullionTracks = getMullionsTrackPrice();

        console.log("mullionTracks", mullionTracks);

        const result = mullionTracks.find(
            (mullion) =>
                (mullion.lenhtMin || 0) <= width &&
                (mullion.lenhtMax || 0) >= width,
        );

        return result ?? mullionTracks[0];
    }

    function getDeliveryPriceMin(appliedTo?: string | string[]): Dictionary[] {
        return filterAppliedTo("deliveryPriceMin", appliedTo);
    }
    function getDeliveryPriceSystemMin(
        appliedTo?: string | string[],
    ): Dictionary[] {
        return filterAppliedTo("deliveryPriceSystemMin", appliedTo);
    }
    function getDeliveryDiscountLimits(
        appliedTo?: string | string[],
    ): Dictionary[] {
        return filterAppliedTo("deliveryDiscountLimits", appliedTo);
    }
    function getGlassWeight(appliedTo?: string | string[]): Dictionary[] {
        return filterAppliedTo("glassWeight", appliedTo);
    }

    function findDeliveryDoorPriceMin(
        systemCode: string,
        doorCode: string | undefined,
        hours: number | undefined,
    ): Dictionary {
        const code = `dpm-${doorCode}-${hours}`;

        console.log("door deliverypricemin code", code);
        return getFromDictByCode(getDeliveryPriceMin(), code);

        // code:"dpm-cgf-div-2"
        // hours:2
        // price_min:1300
        // price_multiplier:14
        // title:"CGF Divider"
    }

    function findDeliveryDividerPriceMin(
        systemCode: string,
        hours: number | undefined,
    ): Dictionary {
        const systemCodeWithoutDigits = systemCode.replace(/\d+/g, "");
        const code = `dpm-${systemCodeWithoutDigits}-div-${hours}`;

        console.log("div deliverypricemin code", code);

        return getFromDictByCode(getDeliveryPriceMin(), code);

        // code:"dpm-cgf-div-2" / "dpm-kmp-div-2"
        // hours:2
        // price_min:1300
        // price_multiplier:14
        // title:"CGF Divider"
    }

    function findDeliveryPriceSystemMin(
        systemCode: string,
        hours: number | undefined,
    ): Dictionary {
        // if hours less then 2, set it to 2 (min delivery time)
        const minHours = hours && hours >= 2 ? hours : 2;
        const systemCodeWithoutDigits = systemCode.replace(/\d+/g, "");
        const code = `dpsm-${systemCodeWithoutDigits}-${minHours}`;

        return getFromDictByCode(getDeliveryPriceSystemMin(), code);

        // code:"dpsm-kmp-2"
        // area:90
        // price_min:4428
        // time:2
        // title:"Kompas"
    }

    function findGlassWeight(
        glassType: string | undefined,
        isSteel: boolean,
        unit: string | undefined,
    ): Dictionary {
        const unitType = isSteel ? unit?.toUpperCase() : "Glass";
        const system = isSteel ? "steel" : "aluminum";
        const code = `${system}-system-${glassType}`;

        const allGlassWeights = getGlassWeight();
        const glassWeightItem = allGlassWeights.find(
            (item) => item.code === code && item.unit === unitType,
        );
        //@ts-ignore
        return glassWeightItem;
        // return getFromDictByCode(getGlassWeight(), code);

        // code:"aluminum-system-3-16"
        // price: 2.46
        // title: "Aluminum system 3/16\""
        // unit: "Glass"
    }
    function findDoorType(code: string | undefined): Dictionary {
        return getFromDictByCode(getDoorTypes(), code);
    }
    function findFinishColor(code: string | undefined): Dictionary {
        return getFromDictByCode(getFinishColors(), code);
    }
    function findStructureTypes(
        code: string | undefined,
        appliedTo: string | string[],
    ): Dictionary {
        return getFromDictByCode(getStructureTypes(appliedTo), code);
    }
    function findGlassSortThicknesses(code: string | undefined): Dictionary {
        return getFromDictByCode(getGlassSorts(), code);
    }

    function findGlassThickness(
        code: string,
        appliedTo: string | undefined,
    ): Dictionary {
        return getFromDictByCode(getGlassThickness(appliedTo), code);
    }

    function findGlassDefaultThick(
        systemCode: string,
        type?: Ref<string, string> | string | undefined,
        isDoor?: boolean | false,
    ): Dictionary {
        const elementType = isDoor ? type : "div";
        const isKompas = KompasSystemCodes.includes(systemCode);
        const systemCodeWithoutDigits = systemCode.replace(/\d+/g, "");
        const system = isKompas ? "kmp" : systemCodeWithoutDigits;
        const code = `gdt-${system}-${elementType}`;

        return getFromDictByCode(getGlassDefaultThick(), code);

        //   code: "gdt-cgs-div",
        //   door_thick: null,
        //   partition_thick: "3-8",
        //   title: "CGS Frameless Divider"
    }

    return {
        dicts,
        loadDict,
        loadAllDicts,
        loadAllDictsByDate,
        loadAllDictsCurrentDay,
        clearAllDicts,
        getUsStates,
        getDealTypes,
        getFinishColors,
        getInstallationPlaces,
        getInstallationUsage,
        getSpaceDescriptions,
        getSystemUsages,
        getManagers,
        getEstimators,
        getClients,
        // estimate
        getStructureTypes,
        getSystemTypes,
        getDoorTypes,
        getGlassTypes,
        getGlassSorts,
        getGlassThickness,
        getHardwareTypes,
        getCostTypes,
        getTransomTypes,
        getHardwareValues,
        getFillerTypes,
        getAluminumMaterialTypes,
        getAdditionalCosts,
        getAdditionalCostValues,
        getAdditionalCostExtras,
        getHorizontalMullionsSizes,
        getVerticalMullionsSizes,
        getMullionsTrackPrice,
        getDeliveryPriceMin,
        getDeliveryPriceSystemMin,
        getDeliveryDiscountLimits,
        findDeliveryDoorPriceMin,
        findDeliveryDividerPriceMin,
        getGlassWeight,
        getGlassDefaultThick,
        findGlassWeight,
        findDeliveryPriceSystemMin,
        findDoorType,
        findFinishColor,
        findStructureTypes,
        findGlassSortThicknesses,
        findGlassDefaultThick,
        findGlassThickness,
        //prices
        findGlassPrice,
        findGlassSort,
        findDoorHardwarePrice,
        findDoorAdditionalCostsPrice,
        findOutOfSquarePrice,
        findAssetsPrice,
        findAluminumFillerPrice,
        findTransomHardwarePrice,
        findMountHardwarePrice,
        findAdditionalCostPrice,
        findHardwarePrice,
        getGlassThicknessIncludeDoorCondition,
        getOverheadCosts,
        findOverheadCostPrice,
        findState,
        findHorizontalMullionsQty,
        findVerticalMullionsQty,
        findMullionTrack,
        findMullionsTrackPrice,
    };
});

export function getFromDictByCode(dict: Dictionary[], code): Dictionary {
    if (!dict) {
        console.error(dict + " is not dictionary");
        return null;
    }
    return dict.find((item) => item.code === code);
}

export function getTitleFromDictByCode(dict: Dictionary[], code): string {
    return getFromDictByCode(dict, code)?.title;
}
